import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { errorMessage } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { useUploadAttachment } from '@/hooks/useAttachments'
import { pendingUploads, usePendingUploads, type PendingUpload } from '@/hooks/usePendingUploads'

/**
 * Shown on a ticket created from /tickets/new when some attachments failed to
 * upload after the ticket was created: retry them (presign → PUT → confirm
 * again) or dismiss. Lives in memory, so it only appears in the tab that
 * created the ticket.
 */
export function PendingUploadsBanner({ ticketId, routeId }: { ticketId: string; routeId: string }) {
  const { t } = useTranslation()
  const pending = usePendingUploads(ticketId)
  const upload = useUploadAttachment(routeId)
  const [busy, setBusy] = useState(false)

  if (pending.length === 0) return null

  async function retry() {
    setBusy(true)
    const still: PendingUpload[] = []
    for (const p of pending) {
      try {
        await upload.mutateAsync({ file: p.file, visibility: p.visibility })
      } catch (err) {
        still.push({ ...p, error: errorMessage(err) })
      }
    }
    pendingUploads.set(ticketId, still)
    setBusy(false)
  }

  return (
    <div role="alert" className="flex items-start justify-between gap-4 rounded-md border border-note-border bg-note-bg px-4 py-3">
      <div className="flex min-w-0 flex-col gap-1">
        <p className="text-meta font-semibold text-note-heading">{t('manualTicket.pending.title', { count: pending.length })}</p>
        <ul className="flex flex-col gap-0.5">
          {pending.map((p) => (
            <li key={p.file.name} className="truncate text-label text-note-text">
              {t('manualTicket.pending.item', { name: p.file.name, error: p.error })}
            </li>
          ))}
        </ul>
      </div>
      <div className="flex shrink-0 gap-2">
        <Button size="sm" variant="outline" onClick={() => pendingUploads.set(ticketId, [])} disabled={busy}>
          {t('common.dismiss')}
        </Button>
        <Button size="sm" onClick={() => void retry()} disabled={busy}>
          {busy ? t('manualTicket.pending.retrying') : t('manualTicket.pending.retry')}
        </Button>
      </div>
    </div>
  )
}
